import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advanced);
dayjs.extend(customParseFormat);
/**
 * Returns true if `maxDate` is a date that comes after `minDate`.
 */
export const isDateRangeValid = (maxDate: string, minDate: string): boolean => (
  !dayjs(maxDate).isBefore(dayjs(minDate))
);

/**
 * Ensures that the `date` parameter is a date that is not represented as '0000-00-00'
*/
export const isValidSQLDate = (date: string | Date): boolean => (!!(dayjs(new Date(date)).isValid() && date !== '0000-00-00'));

/**
 * If minDate is an empty string, returns undefined, otherwise returns an tuple where minDate and maxDate (in this order) are provided converted to YYYY-MM-DD format
 */
export const handleDateRange = (maxDate: string, minDate: string): string[] | undefined => {
  if (minDate === '') {
    return undefined;
  }
  return [dayjs(maxDate).add(1, 'day').format('YYYY-MM-DDTHH:mm:ss'), dayjs(minDate).format('YYYY-MM-DDTHH:mm:ss')];
};

/**
 * Returns a string representation of a timestamp formatted as 'YYYY-MM-DD'
 *
 * Example: 2000-01-01
 */
export const formatDate = (input: number | string): string => (dayjs(input, 'YYYY-M-D', true) && input !== '0000-00-00' ? dayjs(input).format('YYYY-MM-DD') : 'Invalid Date');

/**
 * Returns a string representation of  UNIX EPOCH timestamp converted to GMT
 * formatted as 'YYYY-MM-DD'. The conversion to GMT is done to ensure that the
 * date is not off by a day due to timezone differences.
 *
 * Example: 2000-01-01
 */
export const unixTimeStampGMTformatDate = (input: number): string => dayjs.tz(input, 'GMT').format('YYYY-MM-DD');

/**
 * Returns a string representation of the time from a Date object in the format 'h:mma'
 *
 * Example: 10:00am
 */
export const formatTime = (input: Date): string => dayjs(input).format('h:mma');

/**
 * Returns a string representation of the time from a Date object in the format 'dddd, MMMM D, YYYY'
 *
 * Example: Friday, April 7, 2022
 */
export const formatAutoResponderDate = (input: Date): string => dayjs(input).format('dddd, MMMM D, YYYY');

/**
 * Converts a date into a dayjs supported format.
 *
 * @param {Date} input refers to the date that needs to be changed
 * @param {string} dTFormat format that the date needs to be converted to.
 *
 * Default format is 'dddd, MMMM D, YYYY'. Value can be any dayjs supported
 * format available at https://day.js.org/docs/en/display/format
 *
 * @return {string} representing the date in the new format
 */
export const formatDateTimeCustom = (input: Date, dTFormat: string = 'dddd, MMMM D, YYYY'): string => dayjs(input).format(dTFormat);

/**
 * Used locally to create date strings in 'YYYY-MM-DD' format.
 */
const createPastDateYmd = (daysBefore: number): string => {
  const today: Date = new Date();
  return dayjs(new Date().setDate(today.getDate() - daysBefore)).format('YYYY-MM-DD');
};

/**
 * List of dates used regularly in drop-down lists.
 */
export const dateDifference = {
  last7: createPastDateYmd(7),
  last14: createPastDateYmd(14),
  last30: createPastDateYmd(30),
  last60: createPastDateYmd(60),
  last90: createPastDateYmd(90),
  last180: createPastDateYmd(180),
  last360: createPastDateYmd(365),
};

/**
 * Reformats provided date parameter as 'YYYY-MM-DD HH:mm:ss'
 */
export const getFullDateTime = (date: string): string => dayjs(date).format('YYYY-MM-DD HH:mm:ss');
/**
 * Returns a time string in the format 'HH:mm' (24 hour clock)
 */
export const get24HrTime = (time: string): string => dayjs(`2001/01/01 ${time}`).format('HH:mm');
/**
 * Returns a time string in the format 'h:mm a' (12 hour clock)
 */
export const get12HrTime = (input: string): string => dayjs(`2001/01/01 ${input}`).format('h:mm a');

/**
 * Converts an input date to a dayJs date object using the provided timezone OR guesses the browser timezone.
 */
export const convertToClientTimezone = (input: Date | string | number, clientTimezone?: string): dayjs.Dayjs => (
  dayjs(input).tz(clientTimezone ?? dayjs.tz.guess())
);
/**
 * Formats a given date/string to ISO date time
 */
export const datetimeIsoYyyyMmDdHhMm = (date: Date | string): string => (dayjs(date).format('YYYY-MM-DD hh:mm'));

/**
 * Formats a given date/string to ISO date
 */
export const dateIsoYyyyMmDd = (date: Date | string): string => (dayjs(date).format('YYYY-MM-DD'));
/**
 * Checks if a given date is in the past relative to today
 */
export const isInPast = (date: Date | string): Boolean => (dayjs(date).isBefore(dayjs()));
